/**
 * The descriptor engine (plan sections 1 and 5). ONE `Resolved` value drives both the
 * price and the payload.
 *
 * PURE: `import type` + sibling modules only; no IO, no classes, no hooks, no error classes,
 * no Node or browser globals (purity.test). Importable from server/ and src/.
 * Hooks, legacy.wire and media materialization run server-side in GenerationService.assemble()
 * (R9); nothing here knows about them.
 *
 * Param resolution order, per param (see resolveParam):
 *   absent (undefined | null | blank string) -> default
 *   -> normalize (strings) -> aliases -> parse (R11 numeric strings) -> domain check
 *   -> out of domain: the param's `invalid` policy for the surface (reject | coerce | passthrough)
 * then constraints (in order), then units, SKU and price.
 */
import type {
  Coerce,
  Cond,
  EngineContract,
  GenerationRequest,
  Invalid,
  MaterializedMedia,
  MediaWire,
  Mode,
  ModeSpec,
  ModelDescriptor,
  Param,
  PayloadResult,
  PriceAxes,
  Resolved,
  ResolveResult,
  Scalar,
  Sku,
  Slot,
  Surface,
  Wire,
} from './types';
import { ALL_MODELS, buildCatalogIndex } from './catalog';
import { applyOverride, evaluateRate, roundCredits } from './pricing';
import { applyAliasRules, matchDomainRule } from './predicates';

type Params = Record<string, Scalar | null>;

const NUMERIC_STRING = /^-?\d+(\.\d+)?$/;

/** R11: a finite number, or a string matching /^-?\d+(\.\d+)?$/; anything else is NaN. */
export function toNumber(v: unknown): number {
  if (typeof v === 'number') return Number.isFinite(v) ? v : NaN;
  if (typeof v === 'string' && NUMERIC_STRING.test(v)) return Number(v);
  return NaN;
}

function isScalar(v: unknown): v is Scalar {
  return typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean';
}

/**
 * How legacy CreditService reads a units value: `Math.ceil(units)` coerces with Number(), so
 * '' / whitespace / null / false are 0, true is 1 and ' 7 ' is 7. Anything that coerces to a
 * non-finite number ('5s', objects, 'Infinity') is NaN here: legacy would bill NaN, which no
 * ledger can hold, so callers fall back to the resolved value (the one documented divergence).
 */
export function legacyUnitsNumber(v: unknown): number {
  if (v !== null && typeof v !== 'string' && typeof v !== 'number' && typeof v !== 'boolean') return NaN;
  const n = Number(v);
  return Number.isFinite(n) ? n : NaN;
}

/**
 * Whether a caller-supplied units value is usable: a finite, positive number or numeric string.
 * `""`, whitespace, null, booleans, `"abc"`, 0, negatives and Infinity are not.
 *
 * The SAME predicate as `isUsableVideoSeconds` in config/falModels.ts (the d01af92 hotfix), which
 * this pure module cannot import (falModels imports the catalog and this engine). Equality over
 * an edge corpus is pinned by engine.test ('isUsableUnits == isUsableVideoSeconds').
 */
export function isUsableUnits(raw: unknown): boolean {
  if (typeof raw !== 'number' && typeof raw !== 'string') return false;
  if (typeof raw === 'string' && raw.trim() === '') return false;
  const n = Number(raw);
  return Number.isFinite(n) && n > 0;
}

/**
 * U1-VOCAB (5): legacy distinguishes an ABSENT key from a BLANK value. A missing `duration`
 * lets the route's own destructuring fallback run (editor 5, headless `Number(d) || 8`), while
 * `''` / null reach reserve() as 0 units AND reach the wire as 0 — a different charge and a
 * different payload. The engine used to fold all three into `default`; `presenceOf` separates
 * them so a descriptor can say so with `blankDefault`. Whitespace-only strings count as blank,
 * as they did before (legacy's Number('  ') is 0 too).
 */
export type Presence = 'absent' | 'blank' | 'present';

export function presenceOf(v: unknown): Presence {
  if (v === undefined) return 'absent';
  if (v === null) return 'blank';
  if (typeof v === 'string' && v.trim() === '') return 'blank';
  return 'present';
}

/** Kept as the pre-VOCAB name (blank folded into absent); `presenceOf` is the one to reach for. */
export function isAbsent(v: unknown): boolean {
  return presenceOf(v) !== 'present';
}

/**
 * The value an absent or blank input resolves to (U1-VOCAB (3) and (5)).
 *   blank + `blankDefault` declared (for this surface) -> blankDefault
 *   `defaultBySurface[surface]` declared -> that value
 *   otherwise -> `default`
 * Used verbatim, exactly as `default` always was: no normalize, no alias, no domain check.
 */
export function defaultFor(p: Param, surface: Surface, presence: Presence = 'absent'): Scalar | null {
  if (presence === 'blank' && p.blankDefault !== undefined) {
    const b = p.blankDefault;
    // Scalar | null, or the per-surface object form (a surface it omits falls through).
    if (b === null || typeof b !== 'object') return b;
    const v = b[surface];
    if (v !== undefined) return v;
  }
  const bySurface = p.defaultBySurface;
  if (bySurface !== undefined) {
    const v = bySurface[surface];
    if (v !== undefined) return v;
  }
  return p.default;
}

/** The policy that applies on this surface (R10: the only per-surface knob). */
export function invalidFor(p: Param, surface: Surface): Invalid {
  const pol = p.invalid;
  if (typeof pol === 'object' && pol !== null && 'editor' in pol) return pol[surface];
  return pol;
}

function normalizeValue(p: Param, v: Scalar): Scalar {
  if (typeof v !== 'string' || !p.normalize) return v;
  if (p.normalize === 'trim') return v.trim();
  if (p.normalize === 'lower') return v.trim().toLowerCase();
  return v.trim().toUpperCase();
}

/**
 * Exact `aliases` map first; on a miss, `aliasRules` in declaration order (U1-VOCAB (1)).
 * The exact map always wins, so adding rules can never change an existing entry's answer.
 */
function applyAlias(p: Param, v: Scalar): Scalar {
  if (p.aliases) {
    const key = String(v);
    if (Object.prototype.hasOwnProperty.call(p.aliases, key)) return p.aliases[key];
  }
  return applyAliasRules(p.aliasRules, v);
}

/**
 * Canonical in-domain value for `v`, or undefined when out of domain.
 * Enum matching is type-lenient in one direction only: 5 matches '5', '5' matches 5 (R11),
 * and the result is always the spelling in `values`.
 */
export function inDomain(p: Param, v: Scalar): Scalar | undefined {
  switch (p.type) {
    case 'enum': {
      const values = p.values ?? [];
      for (const x of values) if (x === v) return x;
      for (const x of values) {
        if (typeof x === 'number' && typeof v === 'string' && toNumber(v) === x) return x;
        if (typeof x === 'string' && typeof v === 'number' && String(v) === x) return x;
      }
      return undefined;
    }
    case 'int':
    case 'number': {
      const n = toNumber(v);
      if (Number.isNaN(n)) return undefined;
      if (p.type === 'int' && !Number.isInteger(n)) return undefined;
      if (p.min !== undefined && n < p.min) return undefined;
      if (p.max !== undefined && n > p.max) return undefined;
      if (p.values && !p.values.some((x) => toNumber(x) === n)) return undefined;
      return n;
    }
    case 'bool':
      return typeof v === 'boolean' ? v : undefined;
    case 'string':
      return typeof v === 'string' ? v : undefined;
    default:
      return undefined;
  }
}

/**
 * Applies a coercion. Total: a value it cannot read falls back to the param default. Every
 * numeric result goes back through inDomain, so it comes out in the domain's spelling (a
 * string enum snaps to '10', not 10) and never outside the domain (else the default).
 */
export function coerceValue(p: Param, c: Coerce, v: Scalar, fallbackValue?: Scalar | null): Scalar | null {
  // U1-VOCAB (3): the coercion target is the SAME fallback an absent value would take on this
  // surface, so `defaultBySurface` cannot leave a param with two disagreeing defaults.
  const fb = fallbackValue === undefined ? p.default : fallbackValue;
  if (c.kind === 'default') return fb;
  const n = toNumber(v);
  if (Number.isNaN(n)) return fb;
  if (c.kind === 'round') {
    return inDomain(p, Math.round(n)) ?? fb;
  }
  if (c.kind === 'clamp') {
    return inDomain(p, Math.max(c.min, Math.min(c.max, Math.round(n)))) ?? fb;
  }
  // snap: nearest allowed value; ties go to the larger (up) or smaller (down) one.
  let best: number | undefined;
  for (const x of c.values) {
    if (best === undefined) { best = x; continue; }
    const d = Math.abs(x - n), bd = Math.abs(best - n);
    if (d < bd || (d === bd && (c.ties === 'up' ? x > best : x < best))) best = x;
  }
  return best === undefined ? fb : inDomain(p, best) ?? fb;
}

type ParamOutcome =
  | { ok: true; value: Scalar | null; note?: string }
  | { ok: false; detail: string };

/**
 * Resolves one param. `lenient` forces coercion instead of rejecting (priceSku must be total;
 * a param whose field this mode never emits must not be able to fail the request).
 */
export function resolveParam(p: Param, raw: unknown, surface: Surface, lenient = false): ParamOutcome {
  const presence = presenceOf(raw);
  if (presence !== 'present') return { ok: true, value: defaultFor(p, surface, presence) };
  const fallback = defaultFor(p, surface, 'present');
  if (!isScalar(raw)) {
    const policy = invalidFor(p, surface);
    if (policy === 'reject' && !lenient) return { ok: false, detail: `${p.id}: not a scalar` };
    return { ok: true, value: fallback, note: `coerced:${p.id}:non-scalar->${String(fallback)}` };
  }
  const cooked = applyAlias(p, normalizeValue(p, raw));
  // U1-VOCAB (4): a matching domain rule REPLACES the policy for this value and bypasses the
  // domain check, so it can refuse an in-domain value (Hunyuan's ladder guard) or accept an
  // out-of-domain one. No rule => `canon` and `policy` are exactly what they were before.
  const rule = matchDomainRule(p, surface, cooked);
  const policy = rule ? rule.then : invalidFor(p, surface);
  const canon = rule ? undefined : inDomain(p, cooked);
  if (canon !== undefined) return { ok: true, value: canon };
  if (policy === 'passthrough') return { ok: true, value: raw, note: `passthrough:${p.id}:${String(raw)}` };
  if (policy === 'reject' && !lenient) {
    return { ok: false, detail: `${p.id}: ${JSON.stringify(raw)} is not one of the supported values` };
  }
  const c: Coerce = typeof policy === 'object' ? policy.coerce : { kind: 'default' };
  const value = coerceValue(p, c, raw, fallback);
  return { ok: true, value, note: `coerced:${p.id}:${String(raw)}->${String(value)}` };
}

/**
 * Evaluates a Cond over resolved params. All keys and ops must hold. Pseudo-params:
 * '$mode' reads the mode; '$t2v' reads the request's legacy `textToVideo` fact, or
 * `mode === 't2v'` when the request carries none (U2-MAPPERS, see types.ts Cond).
 */
export function evalCond(
  cond: Cond, params: Readonly<Record<string, Scalar | null | undefined>>, mode: Mode, textToVideo?: boolean,
): boolean {
  for (const key of Object.keys(cond)) {
    const op = cond[key];
    const v = key === '$mode' ? mode : key === '$t2v' ? (textToVideo ?? mode === 't2v') : params[key];
    if (op.eq !== undefined && v !== op.eq) return false;
    if (op.neq !== undefined && v === op.neq) return false;
    if (op.in !== undefined && !op.in.some((x) => x === v)) return false;
    if (op.gt !== undefined || op.gte !== undefined || op.lt !== undefined || op.lte !== undefined) {
      const n = typeof v === 'number' || typeof v === 'string' ? toNumber(v) : NaN;
      if (Number.isNaN(n)) return false;
      if (op.gt !== undefined && !(n > op.gt)) return false;
      if (op.gte !== undefined && !(n >= op.gte)) return false;
      if (op.lt !== undefined && !(n < op.lt)) return false;
      if (op.lte !== undefined && !(n <= op.lte)) return false;
    }
  }
  return true;
}

/** Constraints (H1), in declaration order, single pass. Mutates `params`, returns notes. */
function applyConstraints(desc: ModelDescriptor, params: Params, mode: Mode, textToVideo?: boolean): string[] {
  const notes: string[] = [];
  for (const c of desc.constraints ?? []) {
    if (!evalCond(c.when, params, mode, textToVideo)) continue;
    for (const key of Object.keys(c.force)) {
      if (params[key] !== c.force[key]) {
        params[key] = c.force[key];
        notes.push(`constraint:${c.id}`);
      }
    }
  }
  return notes;
}

/** First SKU whose `when` holds; the last SKU has no `when` (conformance). */
export function selectSku(desc: ModelDescriptor, params: Params, mode: Mode, textToVideo?: boolean): Sku | undefined {
  return desc.pricing.skus.find((s) => !s.when || evalCond(s.when, params, mode, textToVideo));
}

/**
 * The media wires a mode uses on a surface (U2-MAPPERS): `mediaBySurface[surface]` replaces
 * `media` wholesale when declared, else `media`.
 */
export function modeMedia(spec: ModeSpec, surface: Surface): Partial<Record<Slot, MediaWire>> {
  return spec.mediaBySurface?.[surface] ?? spec.media;
}

function mediaCount(media: GenerationRequest['media'], slot: Slot): number {
  const v = media[slot];
  if (Array.isArray(v)) return v.filter(Boolean).length;
  return v ? 1 : 0;
}

/** P2 smart mode. Not used by the legacy paths in P1 (R6: they pass a concrete mode). */
export function chooseModeFor(desc: ModelDescriptor, media: GenerationRequest['media']): Mode {
  const has = (m: Mode) => desc.modes[m] !== undefined;
  const images = mediaCount(media, 'image');
  const refs = mediaCount(media, 'refs');
  if (desc.face === 'image') {
    if (media.mask && has('fill')) return 'fill';
    if ((images > 0 || refs > 0) && has('edit')) return 'edit';
    if (has('t2i')) return 't2i';
  } else {
    if (media.audio && has('lipsync')) return 'lipsync';
    if (images > 0 && media.endImage && has('flf')) return 'flf';
    if (images > 0 && has('i2v')) return 'i2v';
    if (has('t2v')) return 't2v';
  }
  const first = (Object.keys(desc.modes) as Mode[])[0];
  return first ?? 't2v';
}

interface Units { raw: number; resolved: number; billed: number; note?: string }

/**
 * U1-VOCAB (2), the NaN ruling. The engine must never emit a non-finite unit count or price:
 * legacy's `Math.ceil('abc')` reserves NaN credits (the RPC succeeds, the balance does not
 * move, fal still renders — a revenue leak the engine is NOT asked to reproduce), and a NaN
 * that reached `Resolved.credits` would fail GenerationService's `creditsReserved !== credits`
 * invariant for a reason nobody can read. `finiteUnits` makes every arm total: the resolved
 * value, then the units param's own numeric default, then 0. The cases this fires on are
 * DECLARED, not silent — see services/generation/legacy/divergences.ts.
 */
function finiteUnits(n: number, resolved: number, paramDefault: unknown): number {
  if (Number.isFinite(n)) return n;
  if (Number.isFinite(resolved)) return resolved;
  const d = toNumber(paramDefault);
  return Number.isFinite(d) ? d : 0;
}

function computeUnits(desc: ModelDescriptor, rawParams: Readonly<Record<string, unknown>>, params: Params,
  media: GenerationRequest['media']): Units | { missing: string } {
  const u = desc.pricing.units;
  if ('fixed' in u) return { raw: u.fixed, resolved: u.fixed, billed: u.fixed };
  if ('media' in u) {
    const s = media.audioSeconds;
    if (typeof s !== 'number' || !Number.isFinite(s) || s <= 0) return { missing: 'audioSeconds' };
    return { raw: s, resolved: s, billed: s };
  }
  const resolvedRaw = toNumber(params[u.param]);
  const rawInput = rawParams[u.param];
  // 'raw' reproduces legacy reserve(units = body value) for a USABLE body value. Since d01af92
  // legacy never reserves an unusable one: both routes replace "" / null / "abc" / <= 0 /
  // Infinity with their default BEFORE reserving, and wire that same default. So an unusable
  // raw value bills the RESOLVED value — the one this engine wires — never 0: blank used to
  // reserve 0 here while the param default (5 s) reached fal, a paid render for free.
  const rawN = isUsableUnits(rawInput) ? legacyUnitsNumber(rawInput) : resolvedRaw;
  const rawUnits = Number.isNaN(rawN) ? resolvedRaw : rawN;
  const unitParam = desc.params.find((p) => p.id === u.param);
  const fb = unitParam?.default;
  const resolved = finiteUnits(resolvedRaw, resolvedRaw, fb);
  const raw = finiteUnits(rawUnits, resolvedRaw, fb);
  const billed = u.stage === 'raw' ? raw : resolved;
  const note = Number.isFinite(resolvedRaw) && Number.isFinite(rawUnits)
    ? undefined
    : `nan-units:${u.param}:${String(params[u.param])}->${String(billed)}`;
  return { raw, resolved, billed, note };
}

function price(desc: ModelDescriptor, sku: Sku, params: Params, units: number) {
  // flatRate mirrors model_registry.flat_rate: the legacy generic tail skips the ladder for it.
  const ev = evaluateRate(sku.rate, params, sku.registry.flatRate);
  return { credits: roundCredits(desc.pricing.rounding, ev, units), tierKey: ev.tierKey };
}

function emitWire(wire: Wire, value: Scalar, params: Params): [string, unknown] {
  if ('kind' in wire) {
    if (wire.kind === 'imageSize') {
      const v = String(value);
      const c = wire.custom;
      if (c && c.enabled && v === c.whenValue) {
        const width = toNumber(params[c.widthParam]);
        const height = toNumber(params[c.heightParam]);
        if (!Number.isNaN(width) && !Number.isNaN(height)) return [wire.field, { width, height }];
      }
      return [wire.field, Object.prototype.hasOwnProperty.call(wire.presets, v) ? wire.presets[v] : wire.fallback];
    }
    // scale: Wan 2.2 seconds -> num_frames = clamp(round(v * factor))
    return [wire.field, Math.min(wire.max, Math.max(wire.min, Math.round(toNumber(value) * wire.factor)))];
  }
  let v: Scalar = value;
  if (wire.round) {
    const n = toNumber(v);
    if (!Number.isNaN(n)) v = Math.round(n);
  }
  if (wire.map) {
    const key = String(value);
    if (Object.prototype.hasOwnProperty.call(wire.map, key)) v = wire.map[key];
    else if (wire.mapFallback !== undefined) v = wire.mapFallback;
  }
  switch (wire.as) {
    case 'string': return [wire.field, String(v)];
    case 'integer':
    case 'number': {
      const n = toNumber(v);
      return [wire.field, Number.isNaN(n) ? v : n];
    }
    case 'boolean': return [wire.field, typeof v === 'string' ? v === 'true' : Boolean(v)];
    default: return [wire.field, v];
  }
}

/** Builds an engine over a model list (tests inject their own; the app uses the catalog). */
/** priceSku with its working (tests and diagnostics; the pricingParity harness reads it). */
export interface SkuPrice {
  credits: number;
  params: Readonly<Record<string, Scalar | null>>;
  units: number;
  tierKey: string;
}

export interface Engine extends EngineContract {
  byId(id: string): ModelDescriptor | undefined;
  priceSkuDetailed(skuId: string, axes: PriceAxes, multiplier?: number): SkuPrice | undefined;
}

export function createEngine(models: readonly ModelDescriptor[]): Engine {
  const index = buildCatalogIndex(models, []);
  const byId = (id: string) => index.byId.get(id);

  const resolve = (req: GenerationRequest): ResolveResult => {
    const desc = byId(req.modelId);
    if (!desc) return { ok: false, code: 'UNKNOWN_MODEL', detail: `Unknown model '${req.modelId}'` };
    const media = req.media ?? {};
    const mode: Mode = req.mode === 'auto' ? chooseModeFor(desc, media) : req.mode;
    const spec = desc.modes[mode];
    if (!spec) {
      return { ok: false, code: 'UNSUPPORTED_MODE', param: '$mode', detail: `${desc.id} does not support mode '${mode}'` };
    }
    for (const port of spec.ports) {
      if (port.min > 0 && mediaCount(media, port.slot) < port.min) {
        return { ok: false, code: 'MISSING_INPUT', param: port.slot, detail: `${desc.id} ${mode} needs ${port.min} ${port.slot} input(s)` };
      }
    }
    const prompt = typeof req.prompt === 'string' ? req.prompt : '';
    if (desc.prompt.required && prompt.trim() === '') {
      return { ok: false, code: 'MISSING_INPUT', param: 'prompt', detail: `${desc.id} needs a prompt` };
    }
    if (desc.prompt.maxChars !== undefined && prompt.length > desc.prompt.maxChars) {
      return { ok: false, code: 'INVALID_PARAM', param: 'prompt', detail: `prompt exceeds ${desc.prompt.maxChars} characters` };
    }

    const rawParams = req.params ?? {};
    const params: Params = {};
    const notes: string[] = [];
    for (const p of desc.params) {
      const silent = p.onlyModes !== undefined && !p.onlyModes.includes(mode);
      const out = resolveParam(p, rawParams[p.id], req.surface, silent);
      if (!out.ok) return { ok: false, code: 'INVALID_PARAM', param: p.id, detail: out.detail };
      params[p.id] = out.value;
      if (out.note) notes.push(out.note);
    }
    const textToVideo = req.textToVideo;
    notes.push(...applyConstraints(desc, params, mode, textToVideo));

    const units = computeUnits(desc, rawParams, params, media);
    if ('missing' in units) {
      return { ok: false, code: 'MISSING_INPUT', param: units.missing, detail: `${desc.id} is billed by ${units.missing}` };
    }
    if (units.note) notes.push(units.note);
    const sku = selectSku(desc, params, mode, textToVideo);
    if (!sku) return { ok: false, code: 'INVALID_PARAM', detail: `${desc.id}: no SKU matches` };
    const { credits, tierKey } = price(desc, sku, params, units.billed);

    return {
      ok: true,
      value: {
        modelId: desc.id, mode, endpoint: spec.endpoint, surface: req.surface,
        ...(textToVideo !== undefined ? { textToVideo } : {}),
        params,
        rawUnits: units.raw, units: units.resolved, skuId: sku.id, tierKey, credits, notes,
      },
    };
  };

  const buildPayload = (r: Resolved, media: MaterializedMedia, prompt: string): PayloadResult => {
    const desc = byId(r.modelId);
    const spec = desc?.modes[r.mode];
    if (!desc || !spec) throw new Error(`buildPayload: ${r.modelId}/${r.mode} is not in the catalog`);
    const input: Record<string, unknown> = {};
    if (desc.prompt.field) input[desc.prompt.field] = prompt;
    Object.assign(input, desc.fixed ?? {}, spec.fixed ?? {});
    for (const p of desc.params) {
      if (p.wire === false) continue;
      if (p.onlyModes && !p.onlyModes.includes(r.mode)) continue;
      if (p.sendWhen && !evalCond(p.sendWhen, r.params, r.mode, r.textToVideo)) continue;
      const value = r.params[p.id];
      if (value === null || value === undefined) continue;
      const [field, out] = emitWire(p.wire, value, r.params as Params);
      input[field] = out;
    }
    const wires = modeMedia(spec, r.surface);
    for (const slot of Object.keys(wires) as Slot[]) {
      const wire = wires[slot];
      if (!wire) continue;
      const v = media[slot];
      const list = (Array.isArray(v) ? v : v ? [v] : []).filter(Boolean) as string[];
      if (list.length === 0) continue;
      input[wire.field] = wire.many ? (wire.max !== undefined ? list.slice(0, wire.max) : list) : list[0];
    }
    return { endpoint: r.endpoint, input };
  };

  const priceSkuDetailed = (skuId: string, axes: PriceAxes, multiplier?: number): SkuPrice | undefined => {
    // The legacy calculateCost signature carries no provider; P1 prices 'falai' only.
    let found = index.bySku.get(`falai:${skuId}`);
    if (!found) {
      for (const m of index.byId.values()) {
        const sku = m.pricing.skus.find((s) => s.id === skuId);
        if (sku) { found = { model: m, sku }; break; }
      }
    }
    if (!found) return undefined;
    const { model: desc, sku } = found;
    const u = desc.pricing.units;
    const unitParam = 'param' in u ? u.param : undefined;
    const supplied: Record<string, unknown> = {
      resolution: axes.resolution, quality: axes.quality, enableAudio: axes.enableAudio,
    };
    if (unitParam) supplied[unitParam] = axes.units;
    // U1-VOCAB (3): the legacy calculateCost signature carries no surface, and the pricing
    // harness compares at the EDITOR reserve level, so 'editor' stays the default.
    const surface: Surface = axes.surface ?? 'editor';
    const params: Params = {};
    for (const p of desc.params) {
      const out = resolveParam(p, supplied[p.id], surface, true);
      params[p.id] = out.ok ? out.value : defaultFor(p, surface);
    }
    // The SKU is the reserved model id the caller names, so its `when` equalities are facts
    // about this generation (e.g. enableAudio=true for a '-audio' SKU): they hold for the
    // constraints and the rate key. (Integration: carried over from U3's priceSku.)
    for (const [k, c] of Object.entries(sku.when ?? {})) if (c.eq !== undefined && !k.startsWith('$')) params[k] = c.eq;
    // Mode is not a price axis in the legacy signature; constraints over '$mode' see the first mode.
    const mode = (Object.keys(desc.modes) as Mode[])[0] ?? 't2v';
    applyConstraints(desc, params, mode);
    let units: number;
    if ('fixed' in u) units = u.fixed;
    else if ('media' in u) {
      const n = toNumber(axes.units);
      units = Number.isNaN(n) ? 1 : n;
    } else {
      const resolved = toNumber(params[u.param]);
      const rawN = axes.units === undefined ? resolved : legacyUnitsNumber(axes.units);
      const picked = u.stage === 'raw' && !Number.isNaN(rawN) ? rawN : resolved;
      // U1-VOCAB (2): priceSku is TOTAL — it never answers NaN credits (see finiteUnits).
      units = finiteUnits(picked, resolved, desc.params.find((p) => p.id === u.param)?.default);
    }
    const p = price(desc, sku, params, units);
    return { credits: applyOverride(p.credits, multiplier), params, units, tierKey: p.tierKey };
  };

  const priceSku = (skuId: string, axes: PriceAxes, multiplier?: number): number | undefined =>
    priceSkuDetailed(skuId, axes, multiplier)?.credits;

  return {
    byId,
    resolve,
    buildPayload,
    priceSku,
    priceSkuDetailed,
    chooseMode: chooseModeFor,
  };
}

const DEFAULT_ENGINE = createEngine(ALL_MODELS);

export const resolve: EngineContract['resolve'] = DEFAULT_ENGINE.resolve;
export const buildPayload: EngineContract['buildPayload'] = DEFAULT_ENGINE.buildPayload;
export const priceSku: EngineContract['priceSku'] = DEFAULT_ENGINE.priceSku;
export const chooseMode: EngineContract['chooseMode'] = chooseModeFor;
